import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Modal, Text } from '@mantine/core';

import { useDeleteUserLeaveBoard } from 'api/boards';
import { useGetOrganisations } from 'api/organisations';
import { useGetV1Url, useShowNotification } from 'hooks';
import { usePatchUser } from 'api/account';

type Props = {
  opened: boolean;
  onClose: () => void;
};

export const BoardLeaveModal = ({ opened, onClose }: Props) => {
  const { organisationId, boardId } = useParams();
  if (!organisationId || !boardId) throw new Error('Missing URL params');

  const { mutateAsync: deleteUserLeaveBoard } = useDeleteUserLeaveBoard();
  const { mutateAsync: patchUser } = usePatchUser();
  const { data: organisations } = useGetOrganisations();

  const [actionPending, setActionPending] = useState(false);
  const showNotification = useShowNotification();
  const getV1Url = useGetV1Url();

  const { t } = useTranslation('organisations');

  const leaveBoardHandler = async () => {
    setActionPending(true);
    try {
      const defaultOrganisation = organisations?.find(
        ({ isDefault }) => isDefault,
      );
      if (!defaultOrganisation) throw new Error('No default organisation');
      await Promise.all([
        patchUser({ lastBoardId: (defaultOrganisation as any).defaultBoardId }),
        deleteUserLeaveBoard({ pathParams: { boardId } }),
      ]);
      showNotification({
        variant: 'success',
        message: t('organisationView.boards.leave.modal.successMessage'),
      });
      window.location.href = getV1Url('contracts');
    } catch (error) {
      showNotification({
        variant: 'error',
        message: t('organisationView.boards.leave.modal.errorMessage'),
      });
      setActionPending(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('organisationView.boards.leave.modal.title')}
      centered
      size="md"
    >
      <Text fz={13} sx={theme => ({ marginBottom: theme.other.spacing(3) })}>
        {t('organisationView.boards.leave.modal.description')}
      </Text>
      <Flex gap="lg" justify="flex-end">
        <Button variant="default" onClick={onClose}>
          {t('organisationView.boards.leave.modal.cancelButton')}
        </Button>
        <Button loading={actionPending} color="red" onClick={leaveBoardHandler}>
          {t('organisationView.boards.leave.modal.deleteButton')}
        </Button>
      </Flex>
    </Modal>
  );
};
